/* eslint-disable react/require-default-props */
import Image from '@/components/ui/image';
import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { FaDiscord, FaReddit, FaTwitter, FaGithub } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import useFriendlyDatetime from '@/hooks/use-friendly-datetime';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import type { DiscoverSite } from '@/lib/discover-resources';

interface SocialLink {
  name: string;
  icon: IconType;
  url: string;
}

// Social links configuration
const SOCIAL_LINKS: SocialLink[] = [
  {
    name: 'Discord',
    icon: FaDiscord,
    url: 'https://dsc.gg/1anime',
  },
  {
    name: 'Reddit',
    icon: FaReddit,
    url: 'https://reddit.com/r/1AnimeApp',
  },
  {
    name: 'Twitter',
    icon: FaTwitter,
    url: 'https://twitter.com/1AnimeApp',
  },
  {
    name: 'GitHub',
    icon: FaGithub,
    url: 'https://github.com/1anime',
  },
];

interface StatusInfoProps {
  startText: string | null;
  etaText: string | null;
}

function StatusInfo({
  startText,
  etaText,
}: StatusInfoProps): React.ReactElement {
  return (
    <div className="space-y-2 rounded-lg bg-muted/30 p-4 text-sm text-muted-foreground">
      {startText && (
        <p className="flex items-center justify-center gap-2">
          <span className="h-2 w-2 rounded-full bg-primary" />
          Scheduled {startText}
        </p>
      )}
      {etaText && (
        <p className="flex items-center justify-center gap-2">
          <span className="h-2 w-2 rounded-full bg-primary" />
          Estimated return {etaText}
        </p>
      )}
      {!startText && !etaText && (
        <p className="text-muted-foreground">
          Thank you for your patience while we improve your experience.
        </p>
      )}
    </div>
  );
}

interface HeaderProps {
  reason?: string;
  startText: string | null;
  etaText: string | null;
}

function MainHeader({
  reason,
  startText,
  etaText,
}: HeaderProps): React.ReactElement {
  return (
    <div className="rounded-2xl border border-border/50 bg-card/50 p-8 shadow-lg backdrop-blur">
      <div className="mb-6 flex items-center justify-center">
        <div className="rounded-xl bg-primary/10 p-3">
          <Image src="/logo.svg" alt="1Anime" width={64} height={64} />
        </div>
      </div>
      <h1 className="mb-3 text-4xl font-bold tracking-tight">
        We&apos;ll be back soon
      </h1>
      {reason && (
        <p className="mb-6 text-base text-muted-foreground">{reason}</p>
      )}
      <StatusInfo startText={startText} etaText={etaText} />
    </div>
  );
}

function SocialLinks(): React.ReactElement {
  return (
    <>
      <div className="mt-8 flex items-center gap-6">
        {SOCIAL_LINKS.map((link) => {
          const Icon = link.icon;
          return (
            <a
              key={link.name}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              title={link.name}
              className="text-muted-foreground transition-all duration-200 hover:scale-125 hover:text-primary"
              aria-label={link.name}
            >
              <Icon className="h-5 w-5" />
            </a>
          );
        })}
      </div>
      <div className="mt-6 text-xs text-muted-foreground/60">
        Join our community for updates
      </div>
    </>
  );
}

function AlternativeSites({
  isOpen,
  onOpenChange,
  sites,
}: {
  isOpen: boolean;
  onOpenChange: (value: boolean) => void;
  sites: DiscoverSite[];
}): React.ReactElement {
  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <CollapsibleTrigger className="group w-full rounded-lg border border-border/50 bg-card/50 px-6 py-4 transition-all hover:bg-card/70">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-foreground">
            Continue watching on alternative sites
          </span>
          <ChevronDown
            className={`h-5 w-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-4 space-y-3">
        {sites.length > 0 ? (
          sites.map((site) => (
            <a
              key={site.id}
              href={site.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-4 rounded-lg border border-border/30 bg-card/30 p-4 transition-all hover:border-border/60 hover:bg-card/50"
            >
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-foreground transition-colors group-hover:text-primary">
                  {site.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {site.description}
                </p>
              </div>
              <span className="shrink-0 text-xs font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
                Visit →
              </span>
            </a>
          ))
        ) : (
          <div className="rounded-lg border border-dashed border-border/60 bg-card/30 p-4 text-left text-sm text-muted-foreground">
            No alternative or partner sites are available right now.
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

export interface MaintenanceScreenProps {
  reason?: string;
  startAt?: string | null;
  etaAt?: string | null;
}

export function MaintenanceScreen({
  reason = '',
  startAt = null,
  etaAt = null,
}: MaintenanceScreenProps): React.JSX.Element {
  const [isAlternativesOpen, setIsAlternativesOpen] = useState(false);
  const [sites, setSites] = useState<DiscoverSite[]>([]);
  const { formatStart, formatEta } = useFriendlyDatetime();
  const startText = formatStart(startAt || null);
  const etaText = formatEta(etaAt || null);
  const reasonText = reason || '';

  useEffect(() => {
    let mounted = true;

    const loadResources = async () => {
      try {
        const response = await fetch('/api/discover/resources');
        if (!response.ok) {
          if (mounted) {
            setSites([]);
          }
          return;
        }

        const data = await response.json();

        if (!mounted) return;

        setSites(Array.isArray(data?.sites) ? data.sites : []);
      } catch {
        if (mounted) {
          setSites([]);
        }
      }
    };

    loadResources();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div
      className="font-archivo flex min-h-dvh flex-col items-center justify-between bg-gradient-to-br from-background via-background to-background/80 p-6 text-center"
    >
      <div className="mt-12 flex-1" />
      <div className="mx-auto w-full max-w-2xl space-y-8">
        <MainHeader
          reason={reasonText}
          startText={startText}
          etaText={etaText}
        />
        <AlternativeSites
          isOpen={isAlternativesOpen}
          onOpenChange={setIsAlternativesOpen}
          sites={sites}
        />
      </div>
      <SocialLinks />
    </div>
  );
}

export default MaintenanceScreen;

/* eslint-enable react/require-default-props */
